import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { categories } from "@/mocks/categories";

const mainLinks = [
  { label: "首页", to: "/" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
  { label: "常见问题", to: "/faq" },
];

export default function SiteNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setKeyword(searchParams.get("q") ?? "");
  }, [searchParams]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-300/60"
          : "bg-gradient-to-b from-background-50/90 to-transparent border-b border-transparent"
      }`}
    >
      <div className="w-full h-16 md:h-[72px] px-4 md:px-6 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-9 h-9 rounded-md bg-primary-500 flex items-center justify-center text-foreground-950 text-lg transition-transform duration-300 group-hover:scale-105">
            <i className="ri-film-line" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[15px] md:text-base tracking-[0.14em] text-foreground-950 whitespace-nowrap">
              日韩黄色电影
            </span>
            <span className="mt-1 text-[9px] md:text-[10px] tracking-[0.28em] text-foreground-500 uppercase hidden sm:block">
              JK Cinema Archive
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                isActive
                  ? "text-accent-500"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`
            }
          >
            首页
          </NavLink>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className={`px-3.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                location.pathname.startsWith("/category")
                  ? "text-accent-500"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              影片分类
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  categoryOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full mt-2 w-[420px] p-3 rounded-lg bg-background-100 border border-background-300/70 fade-in">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((item) => (
                    <Link
                      key={item.type}
                      to={`/category/${item.type}`}
                      className="flex items-start gap-2.5 px-2.5 py-2 rounded-md hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 mt-0.5 rounded-md bg-primary-500/20 text-primary-700 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-base`} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900">{item.name}</span>
                        <span className="block text-[11px] text-foreground-500 truncate">
                          {item.nameEn}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/category/popular"
                  className="mt-2 flex items-center justify-between px-3 py-2.5 rounded-md bg-background-200/60 text-xs text-foreground-600 hover:text-accent-500 transition-colors cursor-pointer"
                >
                  浏览全部影片
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            )}
          </div>

          {mainLinks.slice(1).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "text-accent-500"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:flex items-center">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、导演或标签"
                className="w-[190px] xl:w-[240px] h-9 pl-9 pr-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="ml-2 h-9 px-3.5 rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-widest uppercase cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            className="lg:hidden w-9 h-9 rounded-md border border-background-300/70 flex items-center justify-center text-foreground-800 cursor-pointer hover:border-primary-500 transition-colors"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-300/60 bg-background-50/95 backdrop-blur-md max-h-[calc(100vh-64px)] overflow-y-auto fade-in">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索影片、导演或标签"
                  className="w-full h-10 pl-9 pr-3 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-500"
                />
              </div>
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-widest uppercase cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 grid grid-cols-2 gap-1">
              {mainLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <p className="mt-4 mb-2 px-1 text-[11px] tracking-[0.2em] uppercase text-foreground-500">
              影片分类
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((item) => (
                <Link
                  key={item.type}
                  to={`/category/${item.type}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className={`${item.icon} text-primary-700`} />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}